import { useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { Calendar } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import type { ScheduleDay, ScheduleProvider } from '@vagusx/clients';
import { AppointmentCard } from './AppointmentCard';
import { useProviderColors } from './hooks/use-provider-colors';

interface CalendarViewProps {
  days: ScheduleDay[];
  providers: ScheduleProvider[];
  isLoading?: boolean;
  onAppointmentClick?: (appointmentId: string) => void;
}

export function CalendarView({
  days,
  providers,
  isLoading,
  onAppointmentClick,
}: CalendarViewProps): React.ReactElement {
  const providerColors = useProviderColors(providers);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  if (isLoading) {
    return <CalendarViewSkeleton />;
  }

  if (days.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-16">
        <Calendar className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">No appointments found</p>
        <p className="text-xs opacity-70">Try adjusting your filters or date range</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full" ref={scrollContainerRef}>
      <div className="p-3 space-y-4">
        {days.map((day) => {
          const date = parseISO(day.date);
          const appointmentCount = day.appointments.length;

          return (
            <div key={day.date} className="relative">
              {/* Sticky Day Header */}
              <div
                data-date={day.date}
                className="sticky top-0 z-10 bg-muted/80 dark:bg-muted/60 backdrop-blur-xs rounded-md px-3 py-1.5 mb-2 border border-border/40"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">
                    {format(date, 'EEEE, MMMM d, yyyy')}
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {appointmentCount} {appointmentCount === 1 ? 'appointment' : 'appointments'}
                  </span>
                </div>
              </div>

              {/* Appointments Grid - prevent horizontal overflow */}
              <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {day.appointments.map((appointment) => {
                  const color = appointment.provider
                    ? providerColors.get(appointment.provider.id)
                    : undefined;

                  return (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      providerColor={color}
                      onClick={() => onAppointmentClick?.(appointment.id)}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

function CalendarViewSkeleton(): React.ReactElement {
  return (
    <div className="p-3 space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i}>
          <Skeleton className="h-8 w-full mb-2 rounded-md" />
          <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[1, 2, 3, 4].map((j) => (
              <Skeleton key={j} className="h-24 rounded-md" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
